// src/pages/Dashboard/UsedTokenByDate.js
import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import axiosInstance from "../../../utils/axiosInstance";
import { Box, Typography, CircularProgress, Alert, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { useChatbots } from "../../../contexts/ChatbotContext";
import TimelineIcon from '@mui/icons-material/Timeline';

Chart.register(...registerables);

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  accent: '#10b981',
};

export default function UsedTokensChart() {
  const { activeChatbot } = useChatbots();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [viewMode, setViewMode] = useState("bySession");
  const [processedData, setProcessedData] = useState(null);
  const { chatbotId: paramsChatbotId } = useParams();

  useEffect(() => {
    if (activeChatbot) fetchData();
    else setChartData(null);
  }, [activeChatbot]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { chatbot_id: paramsChatbotId };
      const response = await axiosInstance.get(`/get-used-token-by-date`, { params });
      processChartData(response.data.data);
    } catch {
      setError("Failed to fetch token data for the selected chatbot.");
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (data) => {
    const bySessionData = data;
    const aggregatedDaily = {};
    const monthly = {};
    const yearly = {};

    data.forEach(({ created_date, input_token, output_token, total_token }) => {
      if (!aggregatedDaily[created_date]) aggregatedDaily[created_date] = { input: 0, output: 0, total: 0 };
      aggregatedDaily[created_date].input += input_token;
      aggregatedDaily[created_date].output += output_token;
      aggregatedDaily[created_date].total += total_token;

      const [year, month] = created_date.split("-");
      const ym = `${year}-${month}`;
      if (!monthly[ym]) monthly[ym] = { input: 0, output: 0, total: 0 };
      monthly[ym].input += input_token;
      monthly[ym].output += output_token;
      monthly[ym].total += total_token;

      if (!yearly[year]) yearly[year] = { input: 0, output: 0, total: 0 };
      yearly[year].input += input_token;
      yearly[year].output += output_token;
      yearly[year].total += total_token;
    });

    setProcessedData({ bySession: bySessionData, aggregatedDaily, monthly, yearly });
    updateChartData(viewMode, { bySession: bySessionData, aggregatedDaily, monthly, yearly }[viewMode]);
  };

  const updateChartData = (mode, data) => {
    let labels, inputTokens, outputTokens, totalTokens;

    if (mode === "bySession") {
      labels = data.map((i) => i.created_date);
      inputTokens = data.map((i) => i.input_token);
      outputTokens = data.map((i) => i.output_token);
      totalTokens = data.map((i) => i.total_token);
    } else {
      const entries = Object.entries(data);
      labels = entries.map(([k]) => k);
      inputTokens = entries.map(([_, v]) => v.input);
      outputTokens = entries.map(([_, v]) => v.output);
      totalTokens = entries.map(([_, v]) => v.total);
    }

    setChartData({
      labels,
      datasets: [
        { label: "Input Tokens", data: inputTokens, backgroundColor: 'rgba(59, 130, 246, 0.8)', borderRadius: 4 },
        { label: "Output Tokens", data: outputTokens, backgroundColor: 'rgba(139, 92, 246, 0.8)', borderRadius: 4 },
        { label: "Total Tokens", data: totalTokens, backgroundColor: 'rgba(16, 185, 129, 0.8)', borderRadius: 4 },
      ],
    });
  };

  const handleViewChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      if (processedData) updateChartData(newMode, processedData[newMode]);
    }
  };

  const options = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          font: { family: fontFamily, size: 12 },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { family: fontFamily, size: 11 } },
      },
      y: {
        stacked: true,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { font: { family: fontFamily, size: 11 } },
      },
    },
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: 'rgba(16,185,129,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <TimelineIcon sx={{ fontSize: '1.2rem', color: colors.accent }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>
              Token Usage Over Time
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
              Track your chatbot's token consumption
            </Typography>
          </Box>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              fontFamily,
              fontSize: '0.8rem',
              textTransform: 'none',
              px: 2,
              py: 0.75,
              borderRadius: '8px !important',
              border: '1px solid rgba(0,0,0,0.1) !important',
              color: colors.textSecondary,
              '&.Mui-selected': {
                bgcolor: 'rgba(16,185,129,0.1)',
                color: colors.accent,
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(16,185,129,0.15)' },
              },
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            },
          }}
        >
          <ToggleButton value="bySession">Session</ToggleButton>
          <ToggleButton value="aggregatedDaily">Daily</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="yearly">Yearly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={24} sx={{ color: colors.accent }} />
          <Typography sx={{ ml: 2, fontFamily, fontSize: '0.9rem', color: colors.textSecondary }}>
            Loading chart data...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ fontFamily, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {!loading && !error && !chartData && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <TimelineIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 2 }} />
          <Typography sx={{ fontFamily, fontSize: '0.95rem', color: colors.textSecondary }}>
            No token data available for this chatbot.
          </Typography>
        </Box>
      )}

      {chartData && (
        <Box sx={{ height: 350 }}>
          <Bar data={chartData} options={options} />
        </Box>
      )}
    </Box>
  );
}
